import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";

interface TypewriterTextProps {
  text: string;
  speed?: number;
  onComplete?: () => void;
  isTyping: boolean;
}

const TypewriterText: React.FC<TypewriterTextProps> = ({
  text,
  speed = 50,
  onComplete,
  isTyping,
}) => {
  const [displayedText, setDisplayedText] = useState<string>("");
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const htmlContainerRef = useRef<HTMLDivElement>(null);

  const stripHtmlTags = (html: string): string => {
    return html.replace(/<\/?[^>]+(>|$)/g, ""); 
  };

  useEffect(() => {
    if (text === "") {
      setDisplayedText("");
      setCurrentIndex(0);
      setIsComplete(false);
    }
  }, [text]);

  useEffect(() => {
    if (!isTyping) {
      setDisplayedText(text);
      setIsComplete(true);
      if (onComplete) onComplete();
      return;
    }

    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + text[currentIndex]);
        setCurrentIndex((prevIndex) => prevIndex + 1);
      }, speed);

      return () => clearTimeout(timeout);
    } else if (!isComplete) {
      setIsComplete(true);
      if (onComplete) onComplete();
    }
  }, [currentIndex, isTyping, text, speed, onComplete, isComplete]);

  return (
    <Box sx={{ position: "relative" }}>
      {!isComplete ? (
        <Typography
          variant="body1"
          component="pre"
          sx={{ whiteSpace: "pre-wrap" }}
        >
          {stripHtmlTags(displayedText)}
          {isTyping && (
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: "0.5em",
                height: "1em",
                backgroundColor: "#666",
                animation: "blink 1s step-end infinite",
                "@keyframes blink": {
                  "from, to": { opacity: 1 },
                  "50%": { opacity: 0 },
                },
              }}
            />
          )}
        </Typography>
      ) : (
        <Box
          ref={htmlContainerRef}
          sx={{ whiteSpace: "pre-wrap" }}
          dangerouslySetInnerHTML={{ __html: text.replace(/\n/g, "<br />") }}        />
      )}
    </Box>
  );
};

export default TypewriterText;
